import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Calendar, Clock, Download, Shield, User } from 'lucide-react';
import SEO from '../../components/SEO';
import RelatedPages from '../../components/RelatedPages';
import LeadMagnetModal from '../../components/LeadMagnetModal';

const fade   = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.1 } } };

export const ARTICLES = [
  {
    slug: 'sistema-multiagente-2027',
    title: 'Qué es un sistema multiagente y por qué tu empresa lo necesitará en 2027',
    excerpt: 'Gartner estima que el 70 % de los sistemas multiagente desplegarán agentes con roles especializados para 2027. Aquí explicamos qué los diferencia de la automatización por reglas y qué significa eso para tu operación.',
    tag: 'Multiagente',
    tagColor: 'text-cyan-300 bg-cyan-400/10 border-cyan-400/25',
    accent: '#00E5FF',
    date: 'Octubre 2026',
    readTime: '7 min de lectura',
    author: 'Equipo Neuralia 369',
    internal: '/sistema-multiagente',
  },
  {
    slug: 'deepseek-vs-gpt-vs-claude',
    title: 'DeepSeek vs GPT vs Claude en automatización empresarial: cuándo usar cada modelo',
    excerpt: 'Una comparativa honesta, sin patrocinio ni sesgo, sobre cuándo DeepSeek reduce costes reales, cuándo GPT ofrece mejor ecosistema y cuándo Claude es la opción correcta para razonamiento profundo.',
    tag: 'Modelos IA',
    tagColor: 'text-violet-300 bg-violet-400/10 border-violet-400/25',
    accent: '#8B5CF6',
    date: 'Octubre 2026',
    readTime: '9 min de lectura',
    author: 'Equipo Neuralia 369',
    internal: '/integraciones/deepseek',
  },
  {
    slug: 'seguridad-agentes-ia-proveedor',
    title: 'Seguridad en agentes de IA: lo que debes exigir a tu proveedor',
    excerpt: 'Verizon DBIR 2025, CrowdStrike 2025 y Microsoft Work Trend Index 2025 coinciden: la superficie de ataque se amplía con cada agente desplegado. Aquí están las 6 preguntas que debes hacer antes de firmar.',
    tag: 'Seguridad',
    tagColor: 'text-red-300 bg-red-400/10 border-red-400/25',
    accent: '#EF4444',
    date: 'Octubre 2026',
    readTime: '8 min de lectura',
    author: 'Equipo Neuralia 369',
    internal: '/seguridad-agentes',
  },
];

export default function RecursosPage() {
  const [openModal, setOpenModal] = useState(false);
  return (
    <>
      <LeadMagnetModal open={openModal} onClose={() => setOpenModal(false)} source="recursos" />
      <SEO
        title="Recursos y blog — Agentes IA, seguridad y modelos de lenguaje | Neuralia 369"
        description="Artículos técnicos sobre sistemas multiagente, comparativa de modelos IA (DeepSeek, GPT, Claude) y seguridad en agentes empresariales. Sin patrocinios, sin sesgo."
        path="/recursos"
      />

      {/* Hero */}
      <section className="relative pt-28 pb-16 bg-[#02060f] border-b border-white/6 overflow-hidden" data-testid="recursos-hero">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/3 w-[500px] h-[400px] rounded-full bg-cyan-400/[0.03] blur-[100px]" />
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fade} className="editorial-kicker mb-5 text-slate-400">
              Recursos · Blog técnico
            </motion.p>
            <motion.h1 variants={fade} className="text-white font-heading text-4xl sm:text-5xl font-black leading-tight mb-5" data-testid="recursos-title">
              Lo que necesitas saber sobre{' '}
              <span className="gradient-text">agentes empresariales</span>
            </motion.h1>
            <motion.p variants={fade} className="text-slate-300 text-lg max-w-2xl">
              Artículos técnicos sobre sistemas multiagente, modelos de lenguaje y seguridad operativa.
              Sin patrocinios, sin sesgo, con datos verificados.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Article grid */}
      <section className="py-16 sm:py-20 bg-[#030a17]" data-testid="recursos-articles-grid">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Checklist download banner */}
          <motion.div variants={fade} className="mb-8 rounded-2xl border border-cyan-400/25 bg-cyan-400/6 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="recursos-checklist-banner">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
                <Shield className="w-4 h-4 text-cyan-300" />
              </div>
              <div>
                <p className="text-white text-sm font-bold">Checklist de Seguridad para Agentes de IA</p>
                <p className="text-slate-400 text-xs mt-0.5">PDF gratuito · 6 pilares · Preguntas de evaluación para CTOs</p>
              </div>
            </div>
            <button
              onClick={() => setOpenModal(true)}
              className="flex-shrink-0 inline-flex items-center gap-2 text-xs font-bold text-cyan-300 border border-cyan-400/30 rounded-full px-4 py-2 hover:bg-cyan-400/10 transition-all"
              data-testid="recursos-checklist-download-btn"
            >
              <Download className="w-3.5 h-3.5" /> Descargar PDF gratis
            </button>
          </motion.div>

          {/* Article list */}
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="space-y-6">
            {ARTICLES.map((article, i) => (
              <motion.article key={article.slug} variants={fade}
                className="group rounded-2xl border border-white/8 bg-white/[0.025] hover:border-white/16 hover:bg-white/[0.04] transition-all duration-300 overflow-hidden"
                data-testid={`recursos-article-card-${i}`}>
                <Link to={`/recursos/${article.slug}`} className="block p-7 sm:p-8">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className={`text-[10px] font-black uppercase tracking-widest border rounded-full px-3 py-1 ${article.tagColor}`}>
                      {article.tag}
                    </span>
                    <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                      <Calendar className="w-3 h-3" /> {article.date}
                    </span>
                    <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                      <Clock className="w-3 h-3" /> {article.readTime}
                    </span>
                    <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                      <User className="w-3 h-3" /> {article.author}
                    </span>
                  </div>
                  <h2 className="text-white font-black text-xl sm:text-2xl leading-snug mb-3 group-hover:text-cyan-200 transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-slate-400 text-sm leading-relaxed mb-5 max-w-3xl">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: article.accent }}>
                    Leer artículo completo <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </motion.article>
            ))}
          </motion.div>

          {/* Cross-links */}
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-12 rounded-2xl border border-white/8 bg-white/[0.02] p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <BookOpen className="w-5 h-5 text-cyan-400" />
              <p className="text-sm text-slate-300">¿Quieres ver el sistema en acción antes de leer?</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/sistema-multiagente" className="text-xs font-semibold text-cyan-300 hover:text-white border border-cyan-400/25 rounded-full px-4 py-2 hover:bg-cyan-400/10 transition-all" data-testid="recursos-link-multiagente">
                Ver Sistema Multiagente →
              </Link>
              <Link to="/seguridad-agentes" className="text-xs font-semibold text-red-300 hover:text-white border border-red-400/25 rounded-full px-4 py-2 hover:bg-red-400/10 transition-all" data-testid="recursos-link-seguridad">
                Ver Seguridad →
              </Link>
              <Link to="/integraciones/deepseek" className="text-xs font-semibold text-violet-300 hover:text-white border border-violet-400/25 rounded-full px-4 py-2 hover:bg-violet-400/10 transition-all" data-testid="recursos-link-deepseek">
                Ver Integraciones →
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      <RelatedPages pages={['sistema-multiagente','seguridad-agentes','soluciones','integraciones/deepseek']} />
    </>
  );
}
